import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

/**
 * Μπάρα «Έχεις αλλαγές που δεν αποθηκεύτηκαν» των σελίδων ρυθμίσεων (12/10/2026). Φαίνεται ΜΟΝΟ
 * όσο η φόρμα έχει αλλαγές· «Αναίρεση» = πίσω στα αποθηκευμένα, «Αποθήκευση» = μόνο ό,τι άλλαξε.
 *
 * Κολλημένη στο κάτω μέρος της οθόνης, στο πλάτος του περιεχομένου: στο κινητό όλο το πλάτος, από
 * md και πάνω μετριέται η στήλη της σελίδας (το σημάδι πιο κάτω) ώστε να μην καλύπτει το πλαϊνό
 * μενού — χωρίς να υποθέτουμε το πλάτος του. Το ίδιο σημάδι κρατά χώρο, για να μην κρύβει η μπάρα
 * το τελευταίο πεδίο.
 */
interface UnsavedChangesBarProps {
  dirty: boolean;
  saving?: boolean;
  /** false: η αποθήκευση δεν επιτρέπεται ακόμη (π.χ. άκυρη τιμή). */
  canSave?: boolean;
  onSave: () => void;
  onReset: () => void;
}

const MD = '(min-width: 768px)';

const UnsavedChangesBar: React.FC<UnsavedChangesBarProps> = ({ dirty, saving = false, canSave = true, onSave, onReset }) => {
  const { t } = useTranslation(['settings', 'common']);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!dirty || !anchor) return;
    const measure = () => {
      const wide = typeof window.matchMedia === 'function' && window.matchMedia(MD).matches;
      const rect = anchor.getBoundingClientRect();
      // Με zoom στη ρίζα (public/index.html, οθόνες < 1366×768) το getBoundingClientRect δίνει px οθόνης,
      // ενώ τα left/width του style είναι CSS px που μικραίνουν ξανά: η μπάρα έβγαινε ~12% αριστερότερα
      // και στενότερη από τη στήλη. offsetWidth = CSS px, άρα ο λόγος τους είναι το zoom (1 χωρίς zoom).
      const k = anchor.offsetWidth > 0 ? rect.width / anchor.offsetWidth : 1;
      setBox(wide && rect.width > 0 ? { left: rect.left / k, width: rect.width / k } : null);
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(anchor);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [dirty]);

  if (!dirty) return null;

  return (
    <>
      <div ref={anchorRef} aria-hidden="true" className="h-24" />
      <div
        role="region"
        aria-label={t('settings:unsaved.region')}
        data-testid="unsaved-changes-bar"
        className="fixed inset-x-0 bottom-0 z-30 md:bottom-4 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 duration-200"
        style={box ? { left: box.left, width: box.width, right: 'auto' } : undefined}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 pt-3 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] md:pb-3 bg-surface-2 border-t md:border border-line md:rounded-2xl shadow-[0_-4px_16px_rgba(0,0,0,0.08)] md:shadow-lg">
          <p className="flex-1 min-w-[12rem] text-sm font-medium text-t1">{t('settings:unsaved.message')}</p>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onReset}
              disabled={saving}
              className="min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('settings:unsaved.reset')}
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || !canSave}
              className="inline-flex items-center justify-center gap-2 min-h-[44px] px-5 rounded-xl text-sm font-semibold bg-brand text-brand-fg hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {saving ? t('common:saving') : t('settings:unsaved.save')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnsavedChangesBar;
